'use client'

import { useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'
import { getAccount } from '@/lib/auth'
import { interpolateTemplate } from '@/lib/interpolate'
import {
  DEFAULT_FIRST_ORDER_NUDGE_DELAY_DAYS,
  DEFAULT_FIRST_ORDER_NUDGE_MESSAGE,
} from '@/lib/firstOrderNudge'

const DEBUG = String(process.env.NEXT_PUBLIC_DEBUG_AUTH || '').toLowerCase() === 'true'
const plog = (...args) => { if (DEBUG) try { console.log('[PAGE][DASHBOARD]', ...args) } catch {} }

// Feature flag: hide Qualifying Users counts (data not accurate for now)
const SHOW_QUALIFYING_USERS = true

const defaultParams = () => ({
  never_ordered_days_after_signup: '',
  no_order_days: '',
  declining_days: '',
  high_value_threshold: '',
  loyalty_milestones: '',
  noResponseNudgeEnabled: true,
  noResponseNudgeDelayDays: String(DEFAULT_FIRST_ORDER_NUDGE_DELAY_DAYS),
  noResponseNudgeMessage: DEFAULT_FIRST_ORDER_NUDGE_MESSAGE,
})

export default function DashboardPage() {
  const router = useRouter()
  const [account, setAccount] = useState(null)
  const [guarding, setGuarding] = useState(true)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [campaigns, setCampaigns] = useState([])
  const [loadingCampaigns, setLoadingCampaigns] = useState(false)
  const [editingCampaign, setEditingCampaign] = useState(null)

  // Workflow form state
  const [enabled, setEnabled] = useState(true)
  const [trigger, setTrigger] = useState('new_signup')
  const [params, setParams] = useState(defaultParams)
  const [messages, setMessages] = useState([
    { id: 1, day: 0, dayType: 'trigger', message: 'Welcome to our store!' },
    { id: 2, day: 3, dayType: 'previous', message: 'Thanks for signing up! Check out our latest products.' },
    { id: 3, day: 7, dayType: 'previous', message: 'Still exploring? Here are some customer favorites you might love!' }
  ])
  const [feedback, setFeedback] = useState(null)
  const [focusedTextarea, setFocusedTextarea] = useState(null)
  const [businessName, setBusinessName] = useState('')
  // Trigger counts state (for qualifying users per trigger)
  const [triggerCounts, setTriggerCounts] = useState(null)
  const [triggerCountsLoading, setTriggerCountsLoading] = useState(false)
  const [triggerCountsError, setTriggerCountsError] = useState(null)

  // Dashboard business metrics (customers, revenue)
  const [metrics, setMetrics] = useState(null)
  const [metricsLoading, setMetricsLoading] = useState(false)
  const [metricsError, setMetricsError] = useState(null)

  // Build API URL with current parameter overrides
  const buildCountsUrl = () => {
    const qs = new URLSearchParams()
    if (params.never_ordered_days_after_signup?.toString().trim()) qs.set('never_ordered_days_after_signup', params.never_ordered_days_after_signup)
    if (params.no_order_days?.toString().trim()) qs.set('no_order_days', params.no_order_days)
    if (params.declining_days?.toString().trim()) qs.set('declining_days', params.declining_days)
    if (params.high_value_threshold?.toString().trim()) qs.set('high_value_threshold', params.high_value_threshold)
    if (params.loyalty_milestones?.toString().trim()) qs.set('loyalty_milestones', params.loyalty_milestones)
    const query = qs.toString()
    return query ? `/api/automation/trigger-counts?${query}` : '/api/automation/trigger-counts'
  }

  // Load dashboard metrics from our API
  const loadMetrics = async () => {
    setMetricsLoading(true)
    try {
      const year = new Date().getFullYear()
      const res = await fetch(`/api/metrics/cleancloud?windowDays=30&year=${year}`, { method: 'GET', credentials: 'include' })
      if (res.ok) {
        const data = await res.json()
        setMetrics(data)
        setMetricsError(null)
      } else {
        setMetricsError('Failed to load metrics')
      }
    } catch (e) {
      setMetricsError('Failed to load metrics')
    } finally {
      setMetricsLoading(false)
    }
  }

  const formatCurrency = (amount) => {
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(amount) || 0)
    } catch {
      const n = Number(amount) || 0
      return `$${n.toFixed(2)}`
    }
  }

  // Shared fetcher for trigger counts
  const fetchTriggerCounts = async () => {
    setTriggerCountsLoading(true)
    try {
      const res = await fetch(buildCountsUrl(), { method: 'GET', credentials: 'include' })
      if (res.ok) {
        const data = await res.json()
        setTriggerCounts(data.counts || {})
        setTriggerCountsError(null)
      } else {
        setTriggerCountsError('Failed to load trigger counts')
      }
    } catch (e) {
      setTriggerCountsError('Failed to load trigger counts')
    } finally {
      setTriggerCountsLoading(false)
    }
  }

  // Insert variable into focused textarea
  const insertVariable = (variable) => {
    if (focusedTextarea) {
      insertVariableInto(focusedTextarea, variable)
      return
    }
    // Fallback to clipboard copy if no textarea is focused
    copyToClipboard(variable)
  }

  // Fallback copy to clipboard function
  const copyToClipboard = async (variable) => {
    try {
      await navigator.clipboard.writeText(variable)
      setFeedback({ type: 'success', text: `Copied ${variable} to clipboard!` })
      setTimeout(() => setFeedback(null), 2000)
    } catch (error) {
      setFeedback({ type: 'error', text: 'Failed to copy to clipboard' })
      setTimeout(() => setFeedback(null), 2000)
    }
  }

  // Insert into a specific textarea element (used by per-message variable chips)
  const insertVariableInto = (textarea, variable) => {
    if (!textarea) return copyToClipboard(variable)
    const start = textarea.selectionStart ?? textarea.value.length
    const end = textarea.selectionEnd ?? textarea.value.length
    const currentValue = textarea.value

    const newValue = currentValue.substring(0, start) + variable + currentValue.substring(end)
    const messageIndex = parseInt(textarea.dataset.messageIndex)
    if (Number.isInteger(messageIndex) && messageIndex >= 0) {
      const updatedMessages = [...messages]
      updatedMessages[messageIndex].message = newValue
      setMessages(updatedMessages)

      // restore focus and caret after state update
      setTimeout(() => {
        try {
          textarea.focus()
          textarea.setSelectionRange(start + variable.length, start + variable.length)
        } catch {}
      }, 0)

      setFeedback({ type: 'success', text: `Inserted ${variable}` })
      setTimeout(() => setFeedback(null), 1500)
    } else {
      copyToClipboard(variable)
    }
  }

  useEffect(() => {
    let mounted = true
    plog('mount, fetching account')
    ;(async () => {
      try { router.prefetch('/subscription') } catch {}
      const acc = await getAccount()
      if (!mounted) return
      plog('account response', Boolean(acc))
      if (!acc) {
        plog('redirect -> /login')
        router.replace('/login')
      }
      else {
        // Subscription guard
        try {
          const res = await fetch('/api/billing/me', { method: 'GET', credentials: 'include', cache: 'no-store' })
          if (res.ok) {
            const data = await res.json()
            const status = (data?.stripe?.status || '').toLowerCase()
            const subscribed = ['active','trialing','past_due'].includes(status)
            if (!subscribed) {
              router.replace('/subscription')
              return
            }
          } else {
            router.replace('/subscription')
            return
          }
        } catch {
          router.replace('/subscription')
          return
        }
        setAccount(acc)
        // Kick off data loads without blocking initial render
        loadCampaigns()
        
        // Check for edit parameter in URL
        const urlParams = new URLSearchParams(window.location.search)
        const editId = urlParams.get('edit')
        if (editId && mounted) {
          // Load specific campaign data; doesn't block initial render
          loadCampaignForEdit(editId)
        }
      }
      setLoading(false)
      setGuarding(false)
    })()
    return () => { mounted = false }
  }, [router])

  // Load CleanCloud settings (business name) for variable interpolation/preview
  useEffect(() => {
    if (!account) return
    ;(async () => {
      try {
        const res = await fetch('/api/settings/cleancloud', { method: 'GET', credentials: 'include' })
        if (res.ok) {
          const data = await res.json()
          setBusinessName(data.businessName || '')
        }
      } catch (e) {
        console.error('Error loading CleanCloud settings:', e)
      }
    })()
  }, [account])

  // Initial load of qualifying user counts after account loads
  useEffect(() => {
    if (!account || !SHOW_QUALIFYING_USERS) return
    fetchTriggerCounts()
  }, [account])

  // Debounced refetch when relevant parameter inputs change
  useEffect(() => {
    if (!account || !SHOW_QUALIFYING_USERS) return
    const handle = setTimeout(() => {
      fetchTriggerCounts()
    }, 400)
    return () => clearTimeout(handle)
    // Watch specific keys so we don't refetch on unrelated state changes
  }, [
    account,
    params.never_ordered_days_after_signup,
    params.no_order_days,
    params.declining_days,
    params.high_value_threshold,
    params.loyalty_milestones
  ])

  const loadCampaignForEdit = async (campaignId) => {
    try {
      const res = await fetch('/api/automation/workflows')
      if (res.ok) {
        const data = await res.json()
        const campaign = data.automations?.find(c => c.$id === campaignId)
        if (campaign) {
          // Parse the campaign data from ruleJson
          let parsedRule = {}
          try {
            parsedRule = JSON.parse(campaign.ruleJson || '{}')
          } catch (e) {
            console.error('Error parsing campaign ruleJson:', e)
            return
          }
          
          // Load campaign data into form
          setEditingCampaign(campaign)
          setEnabled(campaign.enabled)
          setTrigger(parsedRule.trigger || 'new_signup')
          setParams({ ...defaultParams(), ...(parsedRule.params || {}) })
          // Normalize messages to ensure each has a stable unique id
          const normalizedMessages = (parsedRule.messages || [
            { id: 1, day: 1, dayType: 'trigger', message: 'Welcome to our store! Reply STOP to opt out.' }
          ]).map((m, i) => {
            // Preserve an existing id if present; otherwise synthesize a unique one
            const safeId = (m && m.id != null && m.id !== '') ? m.id : `${Date.now()}-${i}`
            // Important: spread original first, then set id so undefined doesn't overwrite
            return { ...m, id: safeId }
          })
          setMessages(normalizedMessages)
          setFeedback(null)
        }
      }
    } catch (error) {
      console.error('Error loading campaign for edit:', error)
    }
  }

  const loadCampaigns = async () => {
    setLoadingCampaigns(true)
    try {
      const res = await fetch('/api/automation/workflows')
      if (res.ok) {
        const data = await res.json()
        setCampaigns(data.automations || [])
      }
    } catch (error) {
      console.error('Error loading campaigns:', error)
    } finally {
      setLoadingCampaigns(false)
    }
  }

  const editCampaign = (campaign) => {
    setEditingCampaign(campaign)
    setEnabled(campaign.enabled)
    setTrigger(campaign.trigger)
    setParams({ ...defaultParams(), ...(campaign.params || {}) })
    // Normalize in case messages lack ids
    const normalizedMessages = (campaign.messages || []).map((m, i) => {
      const safeId = (m && m.id != null && m.id !== '') ? m.id : `${Date.now()}-${i}`
      return { ...m, id: safeId }
    })
    setMessages(normalizedMessages)
    setFeedback(null)
  }

  const cancelEdit = () => {
    setEditingCampaign(null)
    setEnabled(true)
    setTrigger('new_signup')
    setParams(defaultParams())
    setMessages([
      { id: 1, day: 1, dayType: 'trigger', message: 'Welcome to our store! Reply STOP to opt out.' },
      { id: 2, day: 3, dayType: 'previous', message: 'Thanks for signing up! Check out our latest products.' },
      { id: 3, day: 7, dayType: 'previous', message: 'Still exploring? Here are some customer favorites you might love!' }
    ])
    setFeedback(null)
  }

  const deleteCampaign = async (campaignId) => {
    if (!confirm('Are you sure you want to delete this campaign?')) return
    
    try {
      const res = await fetch(`/api/automation/workflows/${campaignId}`, {
        method: 'DELETE'
      })
      
      if (res.ok) {
        setCampaigns(campaigns.filter(c => c.$id !== campaignId))
        setFeedback({ type: 'success', text: 'Campaign deleted successfully!' })
      } else {
        setFeedback({ type: 'error', text: 'Failed to delete campaign.' })
      }
    } catch (error) {
      console.error('Error deleting campaign:', error)
      setFeedback({ type: 'error', text: 'Error deleting campaign.' })
    }
  }

  if (guarding) return null
  if (loading) return null

  // Message sequence helpers
  const addMessage = () => {
    const newMessage = {
      id: Date.now(),
      day: 0,
      dayType: 'previous',
      message: ''
    }
    setMessages([...messages, newMessage])
  }

  const removeMessage = (id) => {
    if (messages.length > 1) {
      setMessages(messages.filter(m => m.id !== id))
    }
  }

  const updateMessage = (id, field, value) => {
    setMessages(messages.map(m => 
      m.id === id ? { ...m, [field]: value } : m
    ))
  }

  const getMessageLabel = (msg, index) => {
    if (index === 0) return `${msg.day} day${msg.day !== 1 ? 's' : ''} from trigger`
    return `${msg.day} day${msg.day !== 1 ? 's' : ''} after previous message`
  }

  const requiresParam = (key) => {
    switch (key) {
      case 'never_ordered':
        return 'never_ordered_days_after_signup'
      case 'no_order_days':
        return 'no_order_days'
      case 'declining_activity':
        return 'declining_days'
      case 'high_value':
        return 'high_value_threshold'
      case 'loyalty_interval':
        return 'loyalty_milestones'
      default:
        return null
    }
  }

  const onSave = async (e) => {
    e?.preventDefault?.()
    setFeedback(null)
    // basic validation
    const requiredParamKey = requiresParam(trigger)
    
    // Validate messages
    const emptyMessages = messages.filter(m => !m.message.trim())
    if (emptyMessages.length > 0) {
      return setFeedback({ type: 'error', text: 'Please fill in all SMS messages.' })
    }
    
    if (requiredParamKey) {
      const val = params[requiredParamKey]
      if (!String(val).trim()) {
        return setFeedback({ type: 'error', text: 'Please fill in the required parameter for this trigger.' })
      }
    }

    // Generate name based on trigger type
    const triggerNames = {
      'new_signup': 'New Signup',
      'never_ordered': 'Never Ordered',
      'no_order_days': 'No Recent Orders',
      'first_order': 'First Order',
      'declining_activity': 'Declining Activity',
      'high_value': 'High Value Customer',
      'loyalty_interval': 'Loyalty Milestones',
    }
    const campaignName = triggerNames[trigger] || 'SMS Campaign'

    // Sanitize params before save
    const sanitizedParams = { ...params }
    if (trigger === 'first_order') {
      const delayDays = Number(sanitizedParams.noResponseNudgeDelayDays)
      const nudgeMessage = String(sanitizedParams.noResponseNudgeMessage || '').trim()
      if (sanitizedParams.noResponseNudgeEnabled && (!Number.isFinite(delayDays) || delayDays <= 0)) {
        return setFeedback({ type: 'error', text: 'Nudge delay must be greater than 0 days.' })
      }
      if (sanitizedParams.noResponseNudgeEnabled && !nudgeMessage) {
        return setFeedback({ type: 'error', text: 'Please enter the no-response nudge message.' })
      }
      sanitizedParams.noResponseNudgeDelayDays = Number.isFinite(delayDays) && delayDays > 0
        ? delayDays
        : DEFAULT_FIRST_ORDER_NUDGE_DELAY_DAYS
      sanitizedParams.noResponseNudgeMessage = nudgeMessage || DEFAULT_FIRST_ORDER_NUDGE_MESSAGE
      delete sanitizedParams.noResponseNudgeMessageIndex
    }

    const payload = {
      name: campaignName,
      enabled,
      trigger,
      params: sanitizedParams,
      messages,
    }

    setSaving(true)
    try {
      const url = editingCampaign 
        ? `/api/automation/workflows/${editingCampaign.$id}`
        : '/api/automation/workflows'
      const method = editingCampaign ? 'PUT' : 'POST'

      let res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })

      // Try to extract server error details even if non-OK
      let body = null
      try { body = await res.clone().json() } catch {}

      // If dynamic [id] route isn't matched in this deploy, fall back to base route PUT with ?id=
      if (!res.ok && editingCampaign && (res.status === 404 || res.status === 405)) {
        const fallbackUrl = `/api/automation/workflows?id=${encodeURIComponent(editingCampaign.$id)}`
        res = await fetch(fallbackUrl, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: editingCampaign.$id, ...payload }),
        })
        try { body = await res.clone().json() } catch {}
      }

      if (!res.ok) {
        const serverMsg = body?.error || body?.message
        const msg = serverMsg ? `Save failed: ${serverMsg} (${res.status})` : `Save failed (${res.status})`
        throw new Error(msg)
      }

      const successText = editingCampaign ? 'Campaign updated successfully!' : 'Campaign created successfully!'
      setFeedback({ type: 'success', text: successText })

      // Wait a moment to show the success message, then redirect to campaigns page
      setTimeout(() => {
        router.push('/campaigns')
      }, 1000)

    } catch (err) {
      plog('save error', err?.message)
      // Show detailed error message from server when available
      const text = err?.message || 'Save failed'
      setFeedback({ type: 'error', text })
    } finally {
      setSaving(false)
    }
  }

  const helpText = {
    new_signup: '🎯 How it works: Automatically sends messages to new users when they create an account. Perfect for welcome sequences and onboarding new customers to your store.',
    never_ordered: '🛒 How it works: Targets users who signed up but never made a purchase after a specified number of days. Great for converting browsers into buyers with special offers.',
    no_order_days: '🔄 How it works: Re-engages existing customers who haven\'t ordered in X days. Ideal for win-back campaigns to bring dormant customers back to your store.',
    first_order: '🎉 How it works: Triggers immediately when a customer makes their very first purchase. Perfect for thank you messages, shipping info, or encouraging repeat purchases.',
    declining_activity: '📉 How it works: Identifies customers who used to order regularly (2+ orders) but have gone quiet for X days. Great for re-engagement with personalized offers.',
    high_value: '💎 How it works: Automatically triggers when a customer\'s total lifetime spending reaches your set threshold. Perfect for VIP treatment and exclusive offers.',
    loyalty_interval: '🏆 How it works: Sends messages when customers hit specific order milestones (e.g., 3rd, 5th, 10th order). Excellent for building loyalty and celebrating customer relationships.',
  }

  const triggerLabel = {
    new_signup: 'New Signup',
    never_ordered: 'Never Ordered',
    no_order_days: 'No Orders for X Days',
    first_order: 'First Order',
    declining_activity: 'Declining Activity',
    high_value: 'High Value',
    loyalty_interval: 'Loyalty Milestones',
  }

  const triggerSummary = () => {
    switch (trigger) {
      case 'never_ordered':
        return `0 purchases after ${params.never_ordered_days_after_signup || 'X'} days since signup`
      case 'no_order_days':
        return `Returning customer; no orders for ${params.no_order_days || 'X'} days`
      case 'first_order':
        return 'Fires on the first purchase'
      case 'declining_activity':
        return `2+ orders; quiet for ${params.declining_days || 'X'} days`
      case 'high_value':
        return `Total spend reaches ${params.high_value_threshold || 'threshold'}`
      case 'loyalty_interval':
        return `Order milestones: ${(params.loyalty_milestones || 'e.g. 3,5,10')}`
      default:
        return 'When a new user signs up'
    }
  }

  const renderTriggerFields = () => {
    switch (trigger) {
      case 'never_ordered':
        return (
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-300">Days After Signup</label>
            <input
              type="number"
              min="0"
              className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100"
              value={params.never_ordered_days_after_signup}
              onChange={(e) => setParams(p => ({ ...p, never_ordered_days_after_signup: e.target.value }))}
              placeholder="e.g. 7"
            />
          </div>
        )
      case 'no_order_days':
        return (
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-300">Days Since Last Order</label>
            <input
              type="number"
              min="0"
              className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100"
              value={params.no_order_days}
              onChange={(e) => setParams(p => ({ ...p, no_order_days: e.target.value }))}
              placeholder="e.g. 30"
            />
          </div>
        )
      case 'first_order':
        return (
          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-md border border-slate-700 bg-slate-900/60 p-3">
              <div>
                <div className="text-sm font-medium text-slate-300">No-Response Nudge</div>
                <div className="text-xs text-slate-400">If the customer doesn’t reply to message 1, send a follow-up after a delay.</div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={Boolean(params.noResponseNudgeEnabled)}
                  onChange={(e) => setParams(p => ({ ...p, noResponseNudgeEnabled: e.target.checked }))}
                />
                <span>Enabled</span>
              </label>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-300">Nudge Delay (Days)</label>
              <input
                type="number"
                min="0.01"
                step="any"
                className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100"
                value={params.noResponseNudgeDelayDays}
                onChange={(e) => setParams(p => ({ ...p, noResponseNudgeDelayDays: e.target.value }))}
                placeholder="e.g. 2"
              />
              <p className="text-xs text-slate-400">Sent once after this many days without a YES or NO reply. Default: 2 days.</p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-300">Nudge Message</label>
              <textarea
                className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100"
                rows={4}
                maxLength={320}
                value={params.noResponseNudgeMessage || ''}
                onChange={(e) => setParams(p => ({ ...p, noResponseNudgeMessage: e.target.value }))}
                placeholder={DEFAULT_FIRST_ORDER_NUDGE_MESSAGE}
              />
              <div className="flex justify-between text-xs text-slate-400">
                <span>Variables: {'{{customerName}}'} {'{{businessName}}'}</span>
                <span>{String(params.noResponseNudgeMessage || '').length}/320</span>
              </div>
              <p className="text-xs text-slate-400">A YES, NO, or STOP reply cancels this pending reminder. No second reminder is sent.</p>
            </div>
          </div>
        )
      case 'declining_activity':
        return (
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-300">Days Since Last Order</label>
            <input
              type="number"
              min="0"
              className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100"
              value={params.declining_days}
              onChange={(e) => setParams(p => ({ ...p, declining_days: e.target.value }))}
              placeholder="e.g. 45"
            />
          </div>
        )
      case 'high_value':
        return (
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-300">High Value Threshold</label>
            <input
              type="number"
              min="0"
              className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100"
              value={params.high_value_threshold}
              onChange={(e) => setParams(p => ({ ...p, high_value_threshold: e.target.value }))}
              placeholder="e.g. 200"
            />
          </div>
        )
      case 'loyalty_interval':
        return (
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-300">Order Milestones</label>
            <input
              type="text"
              className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100"
              value={params.loyalty_milestones}
              onChange={(e) => setParams(p => ({ ...p, loyalty_milestones: e.target.value }))}
              placeholder="e.g. 3,5,10"
            />
            <p className="text-slate-400">Comma-separated order counts. Example: 3,5,10</p>
          </div>
        )
      default:
        return null
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-4">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8 text-center">
          <h1 className="mb-2 text-4xl font-bold text-slate-100">SMS Automation Dashboard</h1>
          <p className="text-slate-400">Create and manage your SMS campaigns</p>
          <p className="text-sm text-slate-500">Signed in as {account?.email}</p>
          {loadingCampaigns && (
            <div className="mt-1 inline-flex items-center gap-2 text-xs text-slate-400">
              <svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
              </svg>
              <span>Loading campaigns…</span>
            </div>
          )}
        </div>

        {/* CleanCloud Metrics Dashboard */}
        {false && (
        <div className="mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Total Customers */}
            <div className="rounded-xl border border-slate-800 bg-slate-900/80 p-4 shadow-xl">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm text-slate-400">Total Customers</div>
                <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z"/>
                  </svg>
                </div>
              </div>
              <div className="text-2xl font-bold text-slate-100 mb-1">
                {metricsLoading ? (
                  <span className="inline-flex items-center gap-2 text-slate-400">
                    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                    </svg>
                    <span>Loading…</span>
                  </span>
                ) : (metrics?.totalCustomers ?? '—')}
              </div>
              <div className="text-xs text-slate-500">registered users{metricsError ? ' · error loading metrics' : ''}</div>
            </div>

            {/* Active Customers */}
            <div className="rounded-xl border border-slate-800 bg-slate-900/80 p-4 shadow-xl">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm text-slate-400">Active Customers</div>
                <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                  </svg>
                </div>
              </div>
              <div className="text-2xl font-bold text-slate-100 mb-1">
                {metricsLoading ? (
                  <span className="inline-flex items-center gap-2 text-slate-400">
                    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                    </svg>
                    <span>Loading…</span>
                  </span>
                ) : (metrics?.activeCustomers ?? '—')}
              </div>
              <div className="text-xs text-slate-500">
                {(() => {
                  const total = metrics?.totalCustomers || 0
                  const active = metrics?.activeCustomers || 0
                  const pct = total > 0 ? Math.round((active / total) * 100) : 0
                  return `${pct}% of total (ordered in last ${metrics?.activeWindowDays ?? 30} days)`
                })()}
              </div>
            </div>

            {/* Inactive Customers */}
            <div className="rounded-xl border border-slate-800 bg-slate-900/80 p-4 shadow-xl">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm text-slate-400">Inactive Customers</div>
                <div className="w-8 h-8 bg-red-500 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
                  </svg>
                </div>
              </div>
              <div className="text-2xl font-bold text-slate-100 mb-1">
                {metricsLoading ? (
                  <span className="inline-flex items-center gap-2 text-slate-400">
                    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                    </svg>
                    <span>Loading…</span>
                  </span>
                ) : (metrics?.inactiveCustomers ?? '—')}
              </div>
              <div className="text-xs text-slate-500">
                {(() => {
                  const total = metrics?.totalCustomers || 0
                  const inactive = metrics?.inactiveCustomers || 0
                  const pct = total > 0 ? Math.round((inactive / total) * 100) : 0
                  return `${pct}% of total`
                })()}
              </div>
            </div>

            {/* Never Ordered */}
            <div className="rounded-xl border border-slate-800 bg-slate-900/80 p-4 shadow-xl">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm text-slate-400">Never Ordered</div>
                <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                  </svg>
                </div>
              </div>
              <div className="text-2xl font-bold text-slate-100 mb-1">
                {metricsLoading ? (
                  <span className="inline-flex items-center gap-2 text-slate-400">
                    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                    </svg>
                    <span>Loading…</span>
                  </span>
                ) : (metrics?.neverOrderedCustomers ?? '—')}
              </div>
              <div className="text-xs text-slate-500">
                {(() => {
                  const total = metrics?.totalCustomers || 0
                  const n = metrics?.neverOrderedCustomers || 0
                  const pct = total > 0 ? Math.round((n / total) * 100) : 0
                  return `${pct}% of total (registered but no orders)`
                })()}
              </div>
            </div>

            {/* Total Revenue */}
            <div className="rounded-xl border border-slate-800 bg-slate-900/80 p-4 shadow-xl">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm text-slate-400">Total Revenue</div>
                <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"/>
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd"/>
                  </svg>
                </div>
              </div>
              <div className="text-2xl font-bold text-slate-100 mb-1">
                {metricsLoading ? (
                  <span className="inline-flex items-center gap-2 text-slate-400">
                    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                    </svg>
                    <span>Loading…</span>
                  </span>
                ) : formatCurrency(metrics?.totalRevenue)}
              </div>
              <div className="text-xs text-slate-500">year-to-date {metrics?.revenueYear || new Date().getFullYear()}</div>
            </div>
          </div>
        </div>
        )}

        {/* Campaign Form Section */}
        {editingCampaign && (
          <div className="mb-4">
            <div className="flex items-center gap-2">
              <p className="text-slate-400">Editing: {triggerLabel[trigger]} Campaign</p>
              <button
                onClick={cancelEdit}
                className="rounded bg-slate-700 px-2 py-1 text-xs text-slate-200 hover:bg-slate-600"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Left: Form */}
        <div className="rounded-xl border border-slate-800 bg-slate-900/80 p-6 shadow-xl">
          <form onSubmit={onSave} className="space-y-6">
            <div>
              <h2 className="text-xl font-semibold">Create SMS Automation</h2>
              <p className="text-sm text-slate-400">Define when to send and what to say.</p>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-300">Trigger</label>
                <select
                  className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100 focus:border-purple-400 focus:ring-1 focus:ring-purple-400/20 focus:outline-none"
                  value={trigger}
                  onChange={(e) => setTrigger(e.target.value)}
                >
                  <option value="new_signup">New Signup</option>
                  <option value="never_ordered">Never Ordered</option>
                  <option value="no_order_days">No Orders for X Days</option>
                  <option value="first_order">First Order</option>
                  <option value="declining_activity">Declining Activity</option>
                  <option value="high_value">High Value</option>
                  <option value="loyalty_interval">Loyalty Milestones</option>
                </select>
                {/* Qualifying user counts per trigger (hidden) */}
                {SHOW_QUALIFYING_USERS && (
                  <div className="mt-2 rounded-lg border border-slate-700 bg-slate-900/60 p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <div className="text-xs uppercase tracking-wide text-slate-400">Qualifying users</div>
                    <button
                      type="button"
                      className="text-xs text-slate-300 hover:text-white"
                      onClick={fetchTriggerCounts}
                    >
                      {triggerCountsLoading ? 'Refreshing…' : 'Refresh'}
                    </button>
                  </div>
                  {triggerCountsLoading && (
                    <div className="text-sm text-slate-400">Loading counts…</div>
                  )}
                  {!triggerCountsLoading && triggerCountsError && (
                    <div className="text-sm text-red-400">{triggerCountsError}</div>
                  )}
                  {!triggerCountsLoading && !triggerCountsError && (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {Object.keys(triggerLabel)
                        .map((key) => (
                          <div
                            key={key}
                            className={`rounded-md border p-2 text-sm ${
                              key === trigger ? 'border-purple-500/60 bg-slate-900' : 'border-slate-700 bg-slate-900/40'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-slate-300">{triggerLabel[key]}</span>
                              <span className="font-semibold text-slate-100">{triggerCounts?.[key] ?? '—'}</span>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                  </div>
                )}
              </div>

              {renderTriggerFields()}

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-slate-300">Message Sequence</label>
                  <button
                    type="button"
                    onClick={addMessage}
                    className="flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-medium text-white hover:opacity-80" style={{backgroundColor: '#8B5CF6'}}
                  >
                    + Add Message
                  </button>
                </div>
                
                {/* Variables moved inline per message below each textarea */}
                
                <div className="space-y-4">
                  {messages.map((msg, index) => (
                    <div key={msg.id} className="rounded-lg border border-slate-700 bg-slate-800/50 p-4">
                      <div className="mb-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium text-white" style={{backgroundColor: '#8B5CF6'}}>
                            {index + 1}
                          </span>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              min="0"
                              className="w-16 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-sm text-slate-100"
                              value={msg.day}
                              onChange={(e) => updateMessage(msg.id, 'day', parseInt(e.target.value) || 0)}
                            />
                            <span className="text-sm text-slate-400">
                              {getMessageLabel(msg, index)}
                            </span>
                          </div>
                        </div>
                        {messages.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeMessage(msg.id)}
                            className="text-slate-400 hover:text-red-400"
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="text-sm text-slate-300">Message</label>
                          <span className="text-xs text-slate-400">{msg.message.length}/320 characters</span>
                        </div>
                        <textarea
                          className="w-full rounded-lg border border-slate-600 bg-slate-900 px-3 py-2 text-slate-100 placeholder:text-slate-500 focus:border-purple-400 focus:ring-1 focus:ring-purple-400/20 focus:outline-none"
                          value={msg.message}
                          onChange={(e) => updateMessage(msg.id, 'message', e.target.value)}
                          onFocus={(e) => setFocusedTextarea(e.target)}
                          onBlur={() => setFocusedTextarea(null)}
                          data-message-index={index}
                          rows={3}
                          placeholder={`Enter message for day ${msg.day}...`}
                        />
                        {index === 0 && (
                          <p className="text-xs text-slate-400">Include an opt-out line like "Reply STOP to opt out" to comply with SMS regulations.</p>
                        )}
                        {/* Inline Variables for this message */}
                        <div className="mt-2">
                          <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">Variables</div>
                          <div className="flex flex-wrap gap-2 text-xs">
                            {[
                              { key: '{{customerName}}', tip: 'Customer name' },
                              { key: '{{businessName}}', tip: businessName || 'Business name' },
                                                            { key: '{{orderDateFormatted}}', tip: 'Order date (MM/DD/YYYY)' },
                            ].map(v => (
                              <code
                                key={v.key}
                                className="rounded bg-slate-900 px-2 py-1 text-purple-300 cursor-pointer hover:bg-slate-800 transition-colors"
                                title={`Click to insert: ${v.tip}`}
                                onMouseDown={(e) => {
                                  e.preventDefault()
                                  const el = document.querySelector(`textarea[data-message-index="${index}"]`)
                                  if (el) {
                                    insertVariableInto(el, v.key)
                                  } else {
                                    copyToClipboard(v.key)
                                  }
                                }}
                              >
                                {v.key}
                              </code>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={enabled}
                    onChange={(e) => setEnabled(e.target.checked)}
                  />
                  <span>Enabled</span>
                </label>
                <button className="inline-flex items-center justify-center rounded-lg px-4 py-2 font-semibold text-white hover:opacity-80 disabled:opacity-60" style={{backgroundColor: '#8B5CF6'}} type="submit" disabled={saving}>
                  {saving ? 'Saving…' : (editingCampaign ? 'Update Campaign' : 'Create Campaign')}
                </button>
              </div>

              {feedback && (
                <div
                  className={
                    `mt-1 text-sm ${
                      feedback.type === 'error'
                        ? 'text-red-400'
                        : feedback.type === 'success'
                        ? 'text-green-400'
                        : 'text-slate-400'
                    }`
                  }
                >
                  {feedback.text}
                </div>
              )}
            </div>
          </form>
        </div>

        {/* Right: Live Preview */}
        <div className="rounded-xl border border-slate-800 bg-slate-900/80 p-6 shadow-xl">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">Preview</h3>
              <p className="text-sm text-slate-400">How your automation will look</p>
            </div>
            <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${enabled ? 'bg-emerald-500/15 text-emerald-300' : 'bg-slate-700 text-slate-300'}`}>
              {enabled ? 'Enabled' : 'Disabled'}
            </span>
          </div>

          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center rounded-md bg-slate-800 px-2.5 py-1 text-xs text-slate-200 ring-1 ring-inset ring-slate-700">{triggerLabel[trigger]}</span>
              <span className="text-xs text-slate-400">{triggerSummary()}</span>
            </div>

            {/* Trigger Explanation */}
            <div className="rounded-lg border border-slate-700 bg-slate-950 p-4">
              <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">How This Trigger Works</div>
              <p className="text-sm text-slate-300 leading-relaxed">{helpText[trigger]}</p>
            </div>

            <div className="space-y-3">
              <div className="text-xs uppercase tracking-wide text-slate-400">Message Sequence</div>
              {messages.map((msg, index) => (
                <div key={msg.id} className="rounded-lg border border-slate-700 bg-slate-950 p-4">
                  <div className="mb-2 flex items-center gap-2">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full text-xs font-medium text-white" style={{backgroundColor: '#8B5CF6'}}>
                      {index + 1}
                    </span>
                    <div className="text-xs text-slate-400">
                      {getMessageLabel(msg, index)}
                    </div>
                  </div>
                  <div className="mb-2 text-xs uppercase tracking-wide text-slate-400">SMS</div>
                  <p className="whitespace-pre-wrap text-slate-100">
                    {interpolateTemplate(
                      msg.message || `Message ${index + 1} preview will appear here…`,
                      {
                        customerName: 'Alex Doe',
                        businessName,
                                                orderDateFormatted: '08/20/2025',
                      }
                    )}
                  </p>
                  <div className="mt-2 text-xs text-slate-400">
                    {msg.message.length}/320 characters
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between text-xs text-slate-400">
              <span>{messages.length} message{messages.length !== 1 ? 's' : ''}</span>
              <span>Trigger: {triggerLabel[trigger]}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
